/**
 * Quantity plans from LP-DAG solution only.
 * Total needed = flow out of source (node 0). Per-trailer = flow on edge source → first event of path.
 */

import type { RouteStructure } from './routeInference';

/** Packing list item with itemType id and quantity (for building requirements) */
export interface PackingListItemForQuantity {
    itemType?: { id: string } | null;
    quantity?: number;
}

/** Per-trailer breakdown for one item type */
export interface PerTrailerPlan {
    quantity: number;
    reason: string;
    events: Array<{ eventId: string; quantity: number }>;
}

/** Quantity plan for one item type across all trailers */
export interface ItemQuantityPlan {
    itemTypeId: string;
    totalNeeded: number;
    perTrailer: Map<string, PerTrailerPlan>;
}

/** DAG node row (index, kind; eventId for event nodes) */
export interface LpDagNodeRowForQuantity {
    index: number;
    kind: 'source' | 'event' | 'dest';
    eventId?: string | null;
}

/** LP node quantity (from solution) */
export interface LpNodeQuantity {
    nodeIndex: number;
    quantity: number;
}

/** LP edge flow (from solution) */
export interface LpEdgeFlow {
    fromNodeIndex: number;
    toNodeIndex: number;
    value: number;
}

/**
 * Build quantity plans from LP solution (DAG + node quantities + edge flows).
 * Source node index is 0. totalNeeded = quantity at source. Per-trailer = flow on edge (0, firstEventNodeIndex) for that trailer's path.
 *
 * @param dagNodes - DAG nodes (index 0 = source)
 * @param routeStructure - Inferred route with trailer paths
 * @param nodeQuantitiesByItemType - itemTypeId -> [{ nodeIndex, quantity }] from LP
 * @param edgeFlowsByItemType - itemTypeId -> [{ fromNodeIndex, toNodeIndex, value }] from LP
 * @param getEventName - Optional: event ID -> name for reason strings
 */
export function quantityPlansFromLpSolution(
    dagNodes: LpDagNodeRowForQuantity[],
    routeStructure: RouteStructure,
    nodeQuantitiesByItemType: Record<string, LpNodeQuantity[]>,
    edgeFlowsByItemType: Record<string, LpEdgeFlow[]>,
    getEventName?: (eventId: string) => string
): Map<string, ItemQuantityPlan> {
    const plans = new Map<string, ItemQuantityPlan>();
    const SOURCE = 0;

    const eventIdToNodeIndex = new Map<string, number>();
    for (const n of dagNodes) {
        if (n.kind === 'event' && n.eventId != null) eventIdToNodeIndex.set(n.eventId, n.index);
    }

    const getFlowOutOfSource = (itemTypeId: string, toNodeIndex: number): number => {
        const flows = edgeFlowsByItemType[itemTypeId];
        if (!flows) return 0;
        const e = flows.find(f => f.fromNodeIndex === SOURCE && f.toNodeIndex === toNodeIndex);
        return e?.value ?? 0;
    };

    const getSourceQuantity = (itemTypeId: string): number => {
        const quantities = nodeQuantitiesByItemType[itemTypeId];
        if (!quantities) return 0;
        const a = quantities.find(q => q.nodeIndex === SOURCE);
        return a?.quantity ?? 0;
    };

    for (const itemTypeId of Object.keys(nodeQuantitiesByItemType)) {
        const totalNeeded = getSourceQuantity(itemTypeId);
        if (totalNeeded <= 0) continue;

        const perTrailer = new Map<string, PerTrailerPlan>();
        const nameFn = getEventName ?? ((id: string) => id);

        for (const [trailerId, path] of Array.from(routeStructure.trailerPaths.entries())) {
            const firstEventSeg = path.segments.find((s): s is typeof s & { kind: 'event' } => s.kind === 'event');
            if (!firstEventSeg) continue;
            const firstEventId = firstEventSeg.eventId;
            const nodeIndex = eventIdToNodeIndex.get(firstEventId);
            if (nodeIndex === undefined) continue;
            const quantity = getFlowOutOfSource(itemTypeId, nodeIndex);
            if (quantity <= 0) continue;

            const eventsOnPath: Array<{ eventId: string; quantity: number }> = [];
            for (const seg of path.segments) {
                if (seg.kind !== 'event') continue;
                eventsOnPath.push({ eventId: seg.eventId, quantity: 0 });
            }
            const parts = eventsOnPath.map(e => nameFn(e.eventId));
            perTrailer.set(trailerId, {
                quantity,
                reason: `LP flow: ${parts.join(' → ')}`,
                events: eventsOnPath,
            });
        }

        plans.set(itemTypeId, {
            itemTypeId,
            totalNeeded,
            perTrailer,
        });
    }

    return plans;
}

/**
 * Build quantity plans from a packing operation's supply flow (tasks).
 * Source nodes are the first numSourceNodes (source storage + source events).
 * Per-"trailer" is per-source: key = getNodeLabel(fromNodeIndex), value = outflow from that source.
 */
export function quantityPlansFromOperationFlows(
    edgeFlowsByItemType: Record<string, LpEdgeFlow[]>,
    numSourceNodes: number,
    getNodeLabel: (nodeIndex: number) => string
): Map<string, ItemQuantityPlan> {
    const plans = new Map<string, ItemQuantityPlan>();
    for (const [itemTypeId, flows] of Object.entries(edgeFlowsByItemType)) {
        if (!flows?.length) continue;
        let totalNeeded = 0;
        const perSource = new Map<string, PerTrailerPlan>();
        for (let nodeIndex = 0; nodeIndex < numSourceNodes; nodeIndex++) {
            const outflow = flows.filter(f => f.fromNodeIndex === nodeIndex && f.value > 0).reduce((s, f) => s + f.value, 0);
            if (outflow <= 0) continue;
            totalNeeded += outflow;
            const label = getNodeLabel(nodeIndex);
            perSource.set(String(nodeIndex), {
                quantity: outflow,
                reason: `From ${label}`,
                events: [],
            });
        }
        if (totalNeeded <= 0) continue;
        plans.set(itemTypeId, {
            itemTypeId,
            totalNeeded,
            perTrailer: perSource,
        });
    }
    return plans;
}
